// src/components/SignupForm/SignUpFormInputs.js
import { InputWithIcon, MessageBox } from "reusableComponents";
import { accountDetailsForSignUp } from "./const";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { countryCodes } from "./CountryCodeSelector/countryCodes";
import CountryCodeSelector from "./CountryCodeSelector";
import AccountTypeDropdown from "./AccontTypeDropdown";
import styles from "./styles.module.scss";

const SignUpFormInputs = ({
  signupInputValues,
  setSignupInputValues,
  status,
  errors,
  setErrors,
  onInputBlur,
  accountType,
  setAccountType,
  userType = "seeker",
  googlePrefilled = false,
  hidePasswordFields = false,
}) => {
  const [t] = useTranslation("global");
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes.find(country => country.code === '+33') || countryCodes[0]
  );
  const [phoneValue, setPhoneValue] = useState('');
  const [localErrors, setLocalErrors] = useState({});

  const accountTypeOptions = [
    { label: t("modal.register.individualAccount"), value: "individual" },
    { label: t("modal.register.businessAccount"), value: "company" }
  ];

  // Convert errors array to object for easier lookup
  useEffect(() => {
    const errorMap = {};
    if (Array.isArray(errors)) {
      errors.forEach(error => {
        if (error.code) {
          errorMap[error.code] = error.message;
        }
      });
    }
    setLocalErrors(errorMap);
  }, [errors]);

  const accountTypeError = userType === "seeker" && Array.isArray(errors)
    ? errors.find((error) => error.code === "account_type")?.message
    : null;

  // Only set default account type for provider, NOT for seeker with Google registration
  useEffect(() => {
    // For provider - always business account
    if (userType === "provider" && !accountType) {
      setAccountType({ label: t("modal.register.businessAccount"), value: "company" });
    }
  }, [userType, accountType, setAccountType, t, googlePrefilled]);

  useEffect(() => {
    if (userType === "seeker" && accountType?.value === "individual") {
      setErrors((prevErrors) => prevErrors.filter(error => error.code !== "social_reason"));
    }
  }, [accountType, userType, setErrors]);

  useEffect(() => {
    const phoneVal = signupInputValues.phone || '';
    const matchedCountry = countryCodes.find(country => phoneVal.startsWith(country.code));
    
    if (matchedCountry) {
      setSelectedCountry(matchedCountry);
      setPhoneValue(phoneVal.replace(matchedCountry.code, '').trim());
    } else {
      setPhoneValue(phoneVal);
    }
  }, []);

  useEffect(() => {
    const fullPhone = phoneValue ? `${selectedCountry.code} ${phoneValue}` : '';
    if (fullPhone !== signupInputValues.phone) {
      setSignupInputValues('phone', fullPhone);
    }
  }, [phoneValue, selectedCountry, setSignupInputValues, signupInputValues.phone]);

  const handleAccountTypeSelect = (option) => {
    setAccountType(option);
    setErrors((prevErrors) => prevErrors.filter(error => error.code !== "social_reason"));
    setSignupInputValues('social_reason', '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSignupInputValues(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    onInputBlur(name, value);
  };

  const handlePhoneChange = (e) => {
    setPhoneValue(e.target.value);
  };

  const handlePhoneBlur = (e) => {
    const fullPhone = `${selectedCountry.code} ${e.target.value}`;
    onInputBlur('phone', fullPhone);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
  };

  const isSocialReasonRequired = () => {
    if (userType === "provider") return true;
    if (userType === "seeker" && accountType?.value === "company") return true;
    return false;
  };

  const getFilteredInputs = () => {
    let filtered = [...accountDetailsForSignUp];
    
    if (hidePasswordFields) {
      filtered = filtered.filter(
        detail => detail.name !== "password" && detail.name !== "password_confirmation"
      );
    }
    
    return filtered;
  };

  const inputs = getFilteredInputs().map((detail) => {
    const fieldError = localErrors[detail.name] || "";
    
    // For phone input
    if (detail.name === "phone") {
      return (
        <div key={detail.placeHolder} className={styles.phoneInputGroup}>
          <div className={`${styles.phoneInputContainer} ${fieldError ? styles.inputError : ''}`}>
            <CountryCodeSelector 
              selectedCountry={selectedCountry} 
              onCountrySelect={handleCountrySelect} 
            />
            <InputWithIcon
              value={phoneValue}
              error={fieldError}
              name={detail.name}
              icon={detail.icon}
              placeholder={detail.placeHolder}
              type={detail.type}
              handleChange={handlePhoneChange}
              handleBlur={handlePhoneBlur}
              className={styles.phoneInput}
              required={detail.required}
            />
          </div>
        </div>
      );
    }

    // For social_reason (company name)
    if (detail.name === "social_reason") {
      const isRequired = isSocialReasonRequired();
      
      // Don't show social_reason for individual accounts
      if (userType === "seeker" && accountType?.value === "individual") {
        return null;
      }
      
      return (
        <InputWithIcon
          key={detail.placeHolder}
          value={signupInputValues[detail.name] || ""}
          error={fieldError}
          name={detail.name}
          icon={detail.icon}
          placeholder={detail.placeHolder}
          type={detail.type}
          handleChange={handleChange}
          handleBlur={handleBlur}
          required={isRequired}
          showRequiredText={isRequired}
          disabled={googlePrefilled && (detail.name === "email" || detail.name === "first_name" || detail.name === "last_name")}
        />
      );
    }

    // For all other inputs
    const isDisabled = googlePrefilled && (detail.name === "email" || detail.name === "first_name" || detail.name === "last_name");
    
    return (
      <InputWithIcon
        key={detail.placeHolder}
        value={signupInputValues[detail.name] || ""}
        error={fieldError}
        name={detail.name}
        icon={detail.icon}
        placeholder={detail.placeHolder}
        type={detail.type}
        handleChange={handleChange}
        handleBlur={handleBlur}
        required={detail.required || false}
        label={detail.label ? t(detail.label) : undefined}
        showRequiredText={detail.required || false}
        disabled={isDisabled}
      />
    );
  });

  return (
    <div className={styles.signupFormInputs}>
      {userType === "seeker" && (
        <AccountTypeDropdown
          options={accountTypeOptions}
          selectedOption={accountType}
          onSelect={handleAccountTypeSelect}
          placeholder={t("modal.register.selectAccountType")}
          required={true}
          showRequiredText={true}
          error={accountTypeError}
          disabled={false}
        />
      )}
      
      {inputs}
      
      {hidePasswordFields && (
        <div className={styles.passwordNote}>
          {t("modal.register.passwordNote")}
        </div>
      )}
    </div>
  );
};

export default SignUpFormInputs;